import React from "react";
import { REGISTER_URL } from "@/lib/site";
import { Check, ArrowRight, Sparkles } from "lucide-react";

const TIERS = [
    {
        id: "one",
        label: "One Workshop",
        price: "RM 99",
        note: "Best for focused support on one research stage.",
        perks: ["Live online session", "Workshop slides", "Certificate of attendance"],
    },
    {
        id: "two",
        label: "Any Two Workshops",
        price: "RM 180",
        note: "Best for proposal or data collection preparation.",
        perks: ["Everything in Package A", "Choose any 2 workshops", "Save RM 18"],
    },
    {
        id: "three",
        label: "Any Three Workshops",
        price: "RM 260",
        note: "Best for researchers moving from proposal to fieldwork.",
        perks: ["Everything above", "Practical templates included", "Save RM 37"],
    },
    {
        id: "four",
        label: "Any Four Workshops",
        price: "RM 349",
        note: "Best for deeper support across most stages.",
        popular: true,
        perks: ["Everything above", "Sample transcript & coding worksheet", "Save RM 47"],
    },
    {
        id: "full",
        label: "Full Five-Workshop Series",
        price: "RM 399",
        note: "Complete journey from idea to publication.",
        featured: true,
        badges: ["Best Value", "Most Recommended", "Complete Research Journey"],
        perks: [
            "All 5 workshops · full pathway",
            "All templates, worksheets & guides",
            "Sample transcript · coding worksheet",
            "Publication readiness checklist",
            "Save RM 96",
        ],
    },
];

export default function Pricing() {
    return (
        <section id="pricing" data-testid="pricing-section" className="py-20 md:py-28 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto mb-14">
                    <div className="font-mono-label text-[color:var(--maroon)]">Pricing</div>
                    <h2
                        className="mt-3 font-serif text-4xl sm:text-5xl text-[color:var(--navy)] tracking-tight leading-tight"
                        style={{ fontWeight: 500 }}
                    >
                        Choose the Package That Fits Your Research Journey.
                    </h2>
                    <div className="hr-gold mt-5 mx-auto" />
                    <p className="mt-5 text-slate-600 leading-relaxed">
                        Join one workshop, a focused bundle, or the complete five-workshop journey.
                        Save more with the full series.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-stretch">
                    {TIERS.map((t, i) => {
                        const isFeatured = t.featured;
                        return (
                            <div
                                key={t.id}
                                data-testid={`pricing-card-${t.id}`}
                                className={`relative flex flex-col rounded-3xl border p-6 transition-all duration-300 hover:-translate-y-1 ${
                                    isFeatured
                                        ? "bg-[color:var(--navy)] text-white border-[color:var(--navy)] shadow-2xl lg:scale-[1.05]"
                                        : "bg-white border-[color:var(--line)] hover:border-[color:var(--maroon)] shadow-sm hover:shadow-md"
                                }`}
                            >
                                {t.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[color:var(--maroon)] text-white font-mono-label px-3 py-1">
                                        Popular
                                    </div>
                                )}
                                {isFeatured && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[color:var(--gold)] text-[color:var(--navy)] font-mono-label px-3 py-1 shadow-sm">
                                        Best Value
                                    </div>
                                )}

                                <div
                                    className={`font-mono-label ${
                                        isFeatured ? "text-white/70" : "text-slate-500"
                                    }`}
                                >
                                    Package {String.fromCharCode(65 + i)}
                                </div>
                                <div
                                    className={`mt-2 font-serif text-lg leading-tight ${
                                        isFeatured ? "text-white" : "text-[color:var(--navy)]"
                                    }`}
                                    style={{ fontWeight: 500 }}
                                >
                                    {t.label}
                                </div>

                                <div className="mt-4 flex items-baseline gap-1">
                                    <span
                                        className={`font-serif text-4xl ${
                                            isFeatured ? "text-white" : "text-[color:var(--navy)]"
                                        }`}
                                    >
                                        {t.price}
                                    </span>
                                </div>
                                <p
                                    className={`mt-2 text-xs leading-relaxed ${
                                        isFeatured ? "text-white/70" : "text-slate-500"
                                    }`}
                                >
                                    {t.note}
                                </p>

                                {isFeatured && t.badges && (
                                    <div className="mt-4 flex flex-wrap gap-1.5" data-testid="pricing-full-badges">
                                        {t.badges.map((b) => (
                                            <span
                                                key={b}
                                                className="inline-flex items-center gap-1 rounded-full bg-[color:var(--gold)]/15 border border-[color:var(--gold)]/40 px-2.5 py-1 text-[10px] font-semibold text-[color:var(--gold-soft)] uppercase tracking-wider"
                                            >
                                                <Sparkles className="h-3 w-3" />
                                                {b}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <ul className="mt-5 space-y-2.5 flex-1">
                                    {t.perks.map((p) => (
                                        <li
                                            key={p}
                                            className={`flex items-start gap-2 text-sm ${
                                                isFeatured ? "text-white/90" : "text-slate-700"
                                            }`}
                                        >
                                            <Check
                                                className={`h-4 w-4 mt-0.5 shrink-0 ${
                                                    isFeatured
                                                        ? "text-[color:var(--gold-soft)]"
                                                        : "text-[color:var(--maroon)]"
                                                }`}
                                            />
                                            <span>{p}</span>
                                        </li>
                                    ))}
                                </ul>

                                <a
                                    href={REGISTER_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`pricing-register-btn-${t.id}`}
                                    className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-xs font-semibold transition-colors ${
                                        isFeatured
                                            ? "bg-[color:var(--gold)] text-[color:var(--navy)] hover:bg-[color:var(--gold-soft)]"
                                            : "btn-outline-navy"
                                    }`}
                                >
                                    Register Now
                                    <ArrowRight className="h-3.5 w-3.5" />
                                </a>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                    <div className="rounded-2xl bg-[color:var(--beige)]/60 border border-[color:var(--line)] p-5 text-sm text-slate-700">
                        <div className="font-mono-label text-[color:var(--maroon)]">
                            All packages include
                        </div>
                        <p className="mt-2">
                            Live online session, workshop slides and certificate of attendance.
                        </p>
                    </div>
                    <div className="rounded-2xl bg-[color:var(--beige)]/60 border border-[color:var(--line)] p-5 text-sm text-slate-700">
                        <div className="font-mono-label text-[color:var(--maroon)]">
                            Full series bonus
                        </div>
                        <p className="mt-2">
                            All templates, worksheets, sample transcript, coding worksheet and
                            publication readiness checklist.
                        </p>
                    </div>
                </div>

                <p className="mt-8 text-center text-xs text-slate-500">
                    Registration is handled via the official Google Form. No internal payment gateway
                    is used on this page.
                </p>
            </div>
        </section>
    );
}
